import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { AppIcon } from "@/components/AppIcon";
import { Link, ShoppingCart, Wifi, Store } from "lucide-react";
import { useNativeApp } from "@/hooks/useNativeApp";
import { useIsMobileOrTablet } from "@/hooks/use-mobile";
import { MobileOnboarding } from "@/components/MobileOnboarding";

// Lazy feature images - decoded off main thread

// Use public folder paths for native app compatibility
const aiRecipeImage = "/images/landing/ai-recipe-generation.webp";
const mealPlanningImage = "/images/landing/meal-planning-new.png";
const pantryManagementImage = "/images/landing/pantry-management.webp";
const recipeManagementImage = "/images/landing/recipe-management.webp";
const heroKitchenImage = "/images/landing/hero-kitchen-optimized.webp";

const Landing = () => {
  const navigate = useNavigate();
  const { isNativeApp } = useNativeApp();
  const isMobileOrTablet = useIsMobileOrTablet();

  if (isNativeApp || isMobileOrTablet) {
    return <MobileOnboarding />;
  }
  return (
    <main className="min-h-screen bg-gradient-to-br from-background to-muted/20 overflow-x-hidden">
      {/* Header */}
      <header className="flex items-center justify-end p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/auth')}
            className="text-muted-foreground hover:text-foreground text-sm sm:text-base"
          >
            Sign In
          </Button>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-4 sm:px-6 text-sm sm:text-base"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-12 sm:mb-20">
          {/* Left Content with Logo */}
          <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
            <div className="mb-6 sm:mb-12 flex justify-center lg:justify-start">
              <AppIcon size={160} className="sm:w-[200px] sm:h-[200px] lg:w-[240px] lg:h-[240px]" />
            </div>
            
            <div className="space-y-4 sm:space-y-6 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-7xl font-semibold text-foreground leading-tight">
                Smart Recipe
                <br />
                <span className="text-muted-foreground font-light">Management</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-lg mx-auto lg:mx-0 leading-relaxed">
                Transform your cooking with AI-powered recipe generation, smart meal planning, 
                and intelligent pantry management. Your culinary journey starts here.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <Button 
                onClick={() => navigate('/auth')}
                size="lg"
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg"
              >
                Start Cooking Smart
              </Button>
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative order-1 lg:order-2">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl h-[250px] sm:h-[350px] lg:h-[500px] xl:h-[600px]">
              <img 
                src={heroKitchenImage}
                srcSet="/images/landing/hero-kitchen-mobile.webp 480w, /images/landing/hero-kitchen-optimized.webp 1024w"
                sizes="(max-width: 768px) 100vw, 50vw"
                alt="Professional kitchen environment" 
                className="w-full h-full object-cover"
                fetchPriority="high"
                width={612}
                height={600}
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent"></div>
              {/* Watermark cover overlay */}
              <div className="absolute bottom-0 right-0 w-32 h-16 bg-gradient-to-tl from-background/90 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Top 4 Features */}
        <div className="mb-12 sm:mb-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground mb-3 sm:mb-4">Everything you need to cook smarter</h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">Powerful features designed for modern home chefs</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={aiRecipeImage} 
                  alt="AI Recipe Generation" 
                  className="w-full h-36 sm:h-48 object-cover"
                  loading="lazy"
                  decoding="async"
                  width={296}
                  height={192}
                />
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 sm:mb-3 text-center">AI Recipe Generation</h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center leading-relaxed">
                    Generate personalized recipes using AI based on your available ingredients, dietary preferences, and cooking skill level.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={mealPlanningImage} 
                  alt="Smart Meal Planning" 
                  className="w-full h-36 sm:h-48 object-cover"
                  loading="lazy"
                  decoding="async"
                  width={296}
                  height={192}
                />
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 sm:mb-3 text-center">Smart Meal Planning</h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center leading-relaxed">
                    Plan your meals for days or weeks ahead with drag-and-drop simplicity. Automatic grocery lists included.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={pantryManagementImage} 
                  alt="Pantry Management" 
                  className="w-full h-36 sm:h-48 object-cover"
                  loading="lazy"
                  decoding="async"
                  width={296}
                  height={192}
                />
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 sm:mb-3 text-center">Pantry Management</h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center leading-relaxed">
                    Keep track of your ingredients, get expiration alerts, and receive recipe suggestions based on what you have.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="group hover:scale-105 transition-all duration-300">
              <div className="bg-card/60 backdrop-blur-sm rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border hover:shadow-xl transition-shadow h-full">
                <img 
                  src={recipeManagementImage} 
                  alt="Recipe Management" 
                  className="w-full h-36 sm:h-48 object-cover"
                  loading="lazy"
                  decoding="async"
                  width={296}
                  height={192}
                />
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 sm:mb-3 text-center">Recipe Management</h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center leading-relaxed">
                    Create, edit, and organize your recipes. Import from URLs or build from scratch with our intuitive editor.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Features */}
        <div className="mb-12 sm:mb-20">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">Plus many more features</h3>
            <p className="text-base sm:text-lg text-muted-foreground">Everything you need for a complete cooking experience</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="text-center p-4 sm:p-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Link className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">URL Import</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">Import recipes from any cooking website</p>
            </div>
            
            <div className="text-center p-4 sm:p-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">Smart Grocery Lists</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">Pantry-aware shopping lists that know what you have</p>
            </div>
            
            <div className="text-center p-4 sm:p-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Store className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">Retailer Integration</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">Direct links to buy ingredients from your favorite stores</p>
            </div>
            
            <div className="text-center p-4 sm:p-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Wifi className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">Offline Access</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">Access your recipes even when you're offline</p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center bg-card/40 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 border">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground mb-3 sm:mb-4">Ready to transform your kitchen?</h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
            Join thousands of home chefs who are already cooking smarter with SAGE. 
            Start your culinary journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button 
              onClick={() => navigate('/auth')}
              size="lg"
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 sm:px-12 py-3 sm:py-4 text-base sm:text-lg"
            >
              Start Free Trial
            </Button>
            {!isNativeApp && (
              <Button 
                onClick={() => navigate('/pricing')}
                size="lg"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 sm:px-12 py-3 sm:py-4 bg-primary/10 text-primary rounded-2xl hover:bg-primary/20 transition-all duration-200 hover:scale-[1.02] font-medium transform text-base sm:text-lg"
              >
                See Plans
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/40 mt-12 sm:mt-20">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Sage. All rights reserved.
            </p>
            <nav className="flex items-center gap-4 sm:gap-6 text-sm">
              <a 
                href="/privacy" 
                onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Privacy
              </a>
              <span className="text-muted-foreground/50">·</span>
              <a 
                href="/terms" 
                onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Terms
              </a>
              <span className="text-muted-foreground/50">·</span>
              <a 
                href="/support" 
                onClick={(e) => { e.preventDefault(); navigate('/support'); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Support
              </a>
              {!isNativeApp && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <a 
                    href="/pricing" 
                    onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Pricing
                  </a>
                </>
              )}
            </nav>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Landing;
